import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { PaginationHelper } from '../../shared/services/pagination.service';
import { VendorInvoiceService } from '../../features/vendor-invoice/services/vendor-invoice.service';
import { PermissionService } from '../../shared/services/permission.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { AppComponent } from '../../app.component';

@Component({
  selector: 'app-vendor-invoice',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    AgGridAngular,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './vendor-invoice.component.html',
  styleUrl: './vendor-invoice.component.scss',
})
export class VendorInvoiceComponent implements OnInit {
  public viewPortSize: boolean = false;
  public rowData: any[] | null = null;
  public loading: boolean = true;
  public error: any = null;
  private searchSubject = new Subject<string>();
  readonly dialog = inject(MatDialog);
  invoiceDashboardData: any;
  projectId: number;
  startDate: any;
  endDate: any;
  dateRangeForm: FormGroup;
  selectedCustomerId: any;
  customerSearchControl = new FormControl();

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Remove time component
    return date ? date <= today : false;
  };

  columnDefs: (
    | {
        headerName: string;
        field: string;
        flex: number;
        valueFormatter?: undefined;
        cellRenderer?: undefined;
        floatingFilter?: undefined;
        filter?: undefined;
      }
    | {
        headerName: string;
        field: string;
        flex: number;
        valueFormatter: (params: any) => string;
        cellRenderer?: undefined;
        floatingFilter?: undefined;
        filter?: undefined;
      }
    | {
        field: string;
        headerName: string;
        flex: number;
        cellRenderer: (params: any) => HTMLDivElement;
        floatingFilter: boolean;
        filter: boolean;
        valueFormatter?: undefined;
      }
  )[];
  page = 0;
  pageSize = 10;
  totalPages = 1;
  pageSizeOptions = [5, 10, 20, 50, 100];
  public paginationPageSizeSelector: number[] | boolean = [10, 25, 50];
  partnersList: any;
  canAddBill$ = this.permissionService.hasPermission(
    ModuleIds.VendorInvoice,
    'Create'
  );
  canEditBill$ = this.permissionService.hasPermission(
    ModuleIds.VendorInvoice,
    'Edit'
  );
  canDeleteBill$ = this.permissionService.hasPermission(
    ModuleIds.VendorInvoice,
    'Delete'
  );

  canEditBill: boolean = false;
  canDeleteBill: boolean = false;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.setAGColumn();
  }

  setAGColumn() {
    this.columnDefs = [
      {
        headerName: 'Bill No',
        field: 'billNo',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Vendor Name',
        field: 'vendorName',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Bill Date',
        field: 'billDate',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Due Date',
        field: 'dueDate',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Total Amount',
        field: 'totalAmount',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Balance',
        field: 'balance',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Payment Status',
        field: 'paymentStatus',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Bill Status',
        field: 'billStatus',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'action',
        headerName: 'Actions',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.alignItems = 'center';
          container.style.gap = '20px';
          console.log(params);

          const status = params.data?.billStatus;

          const isEditable =
            status !== 'Approved' && status !== 'Rejected' && status !== 'Sent';

          if (isEditable) {
            const editIcon = document.createElement('img');
            editIcon.src = 'assets/images/icons/icon-edit.svg';
            editIcon.style.cursor = 'pointer';
            editIcon.setAttribute('data-action', 'edit');
            if(this.canEditBill){
              container.appendChild(editIcon);
            }

            const deleteIcon = document.createElement('img');
            deleteIcon.src = 'assets/images/icons/icon-delete.svg';
            deleteIcon.style.cursor = 'pointer';
            deleteIcon.setAttribute('data-action', 'delete');
            if(this.canDeleteBill){
              container.appendChild(deleteIcon);
            }
          }

          // this.store
          //   .select(selectPermissions.hasPermission('8', Permission.Delete))
          //   .subscribe((canDelete) => {
          //     if (canDelete) {
          //       container.appendChild(deleteIcon);
          //     }
          //   });
          return container;
        },
        floatingFilter: false,
        filter: false,
      },
    ];
  }
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public themeClass: string = 'ag-theme-quartz';

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private vendorInvoiceService: VendorInvoiceService,
    private datePipe: DatePipe,
    private appComponent: AppComponent,
    private fb: FormBuilder,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    // this.getAllInvoices();
    this.getAllVendorInvoices();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getAllVendorInvoices(searchTerm);
      });

    this.checkViewportSize();

    this.dateRangeForm = this.fb.group({
      startDate: [''],
      endDate: [''],
      customerName: [''],
    });

    this.canEditBill$.subscribe(val => {
      this.canEditBill = val;
    });
    this.canDeleteBill$.subscribe(val => {
      this.canDeleteBill = val;
    });

    this.appComponent?.toggleSidebar();
    
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.pageSize = 10;
    this.page = 0;
    this.searchSubject.next(searchTerm);
  }

  nextPage() {
    if (this.page + 1 < this.totalPages) {
      this.page++;
      this.getAllVendorInvoices('', this.pageSize);
    }
  }

  prevPage() {
    if (this.page > 0) {
      this.page--;
      this.getAllVendorInvoices('', this.pageSize);
    }
  }

  onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    console.log('New Page Size:', newSize);
    this.pageSize = newSize;
    this.page = 0;
    this.getAllVendorInvoices('', this.pageSize);
  }

  onDateChange(event: any, type: string) {
    const formattedDate = this.datePipe.transform(event.value, 'yyyy-MM-dd');
    if (type === 'start') {
      this.startDate = formattedDate;
    } else if (type === 'end') {
      this.endDate = formattedDate;
    }
    if (this.startDate != null && this.endDate != null) {
      this.getAllVendorInvoices();
    }
  }

  getAllVendorInvoices(search: string = '', pageSize: number = 10): void {
    this.loading = true;

    if (pageSize) {
      this.pageSize = pageSize;
    }

    const skip = this.page * this.pageSize;

    const input = {
      limit: this.pageSize,
      skip: skip,
      search: search,
      startDate: this.startDate,
      endDate: this.endDate,
    };

    this.vendorInvoiceService.getAllVendorInvoices(input).subscribe({
      next: (data) => {
        const bills =
          data?.bills || data?.findAllBillsWithPagination?.bills || [];

        this.totalPages = Math.ceil(
          (data?.total || bills.length) / this.pageSize
        );


        this.rowData = bills.map((bill: any) => {
          return {
            id: bill?.id,
            billNo: bill?.billNo || '--',
            balance:
              bill?.balance != null
                ? `AED ${Number(bill.balance).toFixed(2)}`
                : '--',
            totalAmount:
              bill?.totalAmount != null
                ? `AED ${Number(bill.totalAmount).toFixed(2)}`
                : '--',
            vendorName: bill?.vendorData?.vendorName || '--',
            billDate: bill?.billDate
              ? new Date(bill.billDate).toLocaleDateString('en-GB')
              : '--',
            dueDate: bill?.dueDate
              ? new Date(bill.dueDate).toLocaleDateString('en-GB')
              : '--',
            billStatus: bill?.billStatus || '--',
            paymentStatus: bill?.paymentStatus || '--',

          };
        });

        this.paginationPageSizeSelector = this.rowData?.length
          ? PaginationHelper.getPaginationPageSizeOptions(this.rowData)
          : [];

        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }



  openEdit(billData: any) {
    const invoiceId = billData?.id;
    if (invoiceId) {
      this.router.navigate([`/vendor-invoice/vendor-invoice-edit/${invoiceId}`]);
    }
  }

  onDeleteInvoice(billData: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Confirm Cancel',
        message: 'Are you sure you want to cancel this bill?',
        confirmText: 'Confirm',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.removeVendorInvoice(billData?.id);
      }
    });
  }

  private removeVendorInvoice(billID: number): void {
    this.vendorInvoiceService.removeBill(Number(billID)).subscribe({
      next: () => {
        this.toastr.success('Bill removed successfully.');
        this.getAllVendorInvoices();
      },
      error: (error) => {
        this.toastr.error(
          error.message || 'Failed to remove bill. Please try again.'
        );
      },
    });
  }

  onCellClicked(params: any) {
    if (
      params.column.colId === 'action' &&
      params.event.target.dataset.action
    ) {
      let action = params.event.target.dataset.action;

      if (action === 'edit') {
        this.openEdit(params?.data);
      } else if (action === 'delete') {
        this.onDeleteInvoice(params?.data);
      }
    } else {
      const id = params.data?.id;
      this.router.navigate([`vendor-invoice/vendor-invoice-details`], {
        queryParams: { invoiceId: id },
      });
    }
  }

  onClear() {
    this.startDate = null;
    this.endDate = null;
    this.selectedCustomerId = null;
    this.dateRangeForm.reset();
    this.getAllVendorInvoices();
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  isDropdownOpen = false;

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
  }
}
